// services
import Format from 'modules/Format';
// typings
import math from 'exact-math';
import {
  AveragePrices,
  NetQuantities,
  PnlAveragePrices,
  PositionBase,
  RealizedValues,
  TotalPnl,
  UnrealizedValues,
} from '../typings';

class PositionsCalculation {
  getAvgPnlSum = (
    positions: PositionBase[],
    ltps: {
      [k: string]: {
        lastTraded: string;
        lastTradedPrevious: string;
        cp: string;
      };
    },
  ) => {
    const sum = positions.reduce((acc, position) => {
      const {
        instrument: { e: segment, t: token },
      } = position;
      const exchangeToken = `${segment}|${token}`;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const { lastTraded: ltp, cp } = ltps[exchangeToken];

      const {
        total: { mtm: dailyPnl },
      } = this.getMtmAndPnlValues(position, ltp, cp);
      const sumPnl = ltp ? dailyPnl : 0;

      return Number(acc) + Number(sumPnl);
    }, 0);

    return Format.formatToIndianFloat(sum, 2);
  };

  // Net Buy/Sell quantity values
  getNetQuantity = ({ netInfo }: PositionBase): NetQuantities => ({
    netSellQty: netInfo?.sellQty ?? 0,
    netBuyQty: netInfo?.buyQty ?? 0,
    netQty: (netInfo?.buyQty ?? 0) - (netInfo?.sellQty ?? 0),
  });

  getNetQty = (position: PositionBase) =>
    math.sub(position.netInfo.buyQty, position.netInfo.sellQty);

  // Return data for realized and unrealized MTM and PNL
  getMtmAndPnlValues = (
    position: PositionBase,
    ltp: number | string,
    cp: number | string,
  ): RealizedValues & UnrealizedValues & TotalPnl => {
    const { avgPnlSellPrice, avgPnlBuyPrice } = this.getPnlAvg(position);
    const { netSellQty, netBuyQty, netQty } = this.getNetQuantity(position);
    const { avgSellPrice, avgBuyPrice } = this.getAvgPrices(
      position,
      Number(cp),
    );

    const multiplier = Number(position.instrument.m);

    const pnlOperand =
      netQty > 0 ? Number(ltp) - avgPnlBuyPrice : Number(ltp) - avgPnlSellPrice;
    const mtmOperand =
      netQty > 0 ? Number(ltp) - avgBuyPrice : Number(ltp) - avgSellPrice;

    const realized = {
      pnl:
        (avgPnlSellPrice - avgPnlBuyPrice) *
        Math.min(netBuyQty, netSellQty) *
        multiplier,
      mtm:
        (avgSellPrice - avgBuyPrice) *
        Math.min(netBuyQty, netSellQty) *
        multiplier,
    };

    const unrealized = {
      pnl: netQty * pnlOperand * multiplier,
      mtm: netQty * mtmOperand * multiplier,
    };

    return {
      realized,
      unrealized,
      total: {
        pnl: unrealized.pnl + realized.pnl,
        mtm: unrealized.mtm + realized.mtm,
      },
    };
  };

  // Average Buy/Sell prices values
  getAvgPrices = (position: PositionBase, cp: number): AveragePrices => {
    const { netSellQty, netBuyQty } = this.getNetQuantity(position);
    const {
      fillInfo: { dayBuy, daySell, cfSell, cfBuy },
      instrument: { m = 1 },
    } = position;

    const multiplier = Number(m);

    const avgSellPrice = netSellQty
      ? (Number(daySell.amt) + Number(cfSell.qty) * cp * multiplier) /
        (netSellQty * multiplier)
      : 0;

    const avgBuyPrice = netBuyQty
      ? (Number(dayBuy.amt) + Number(cfBuy.qty) * cp * multiplier) /
        (netBuyQty * multiplier)
      : 0;

    return {
      avgSellPrice: Number.isNaN(avgSellPrice) ? 0 : avgSellPrice,
      avgBuyPrice: Number.isNaN(avgBuyPrice) ? 0 : avgBuyPrice,
    };
  };

  // Profit & Loss average values
  getPnlAvg = (position: PositionBase): PnlAveragePrices => {
    const {
      fillInfo: { dayBuy, daySell, cfSell, cfBuy },
      instrument: { m = 1 },
    } = position;
    const { netSellQty, netBuyQty } = this.getNetQuantity(position);

    const multiplier = Number(m);

    const avgPnlSellPrice = netSellQty
      ? (Number(daySell.amt) +
          Number(cfSell.qty) * Number(cfSell.avgPrice) * multiplier) /
        (netSellQty * multiplier)
      : 0;
    const avgPnlBuyPrice = netBuyQty
      ? (Number(dayBuy.amt) +
          Number(cfBuy.qty) * Number(cfBuy.avgPrice) * multiplier) /
        (netBuyQty * multiplier)
      : 0;

    return {
      avgPnlSellPrice,
      avgPnlBuyPrice,
    };
  };
}

export default PositionsCalculation;

export const PositionsCalculationService = new PositionsCalculation();
